import {taillightsSyntaxColorVariables} from '@github-ui/taillights-worker/styles'
import type {TaillightsSyntaxColors} from '@github-ui/taillights-worker'
import type {CSSProperties} from 'react'
import {clsx} from 'clsx'
import blockStyles from './taillight.module.css'

export const TAILLIGHTS_LINE_HEIGHT = 20
export const TAILLIGHTS_PADDING_INLINE = 10

export type TaillightsRenderingOptions = {
  font?: string
  lineHeight?: number
  paddingInline?: number
  tabSize?: number
  defaultColor?: string
  colors?: TaillightsSyntaxColors
}

export const taillightsCSSColors = Object.fromEntries(
  Object.entries(taillightsSyntaxColorVariables).map(([selector, variable]) => [
    selector,
    `var(${variable}, var(--fgColor-default))`,
  ]),
)

/** Maps custom syntax keys to CSS identifiers without changing the worker's color-key contract. */
export function taillightsColorVariable(selector: string): string {
  if (Object.hasOwn(taillightsSyntaxColorVariables, selector)) {
    const variable = taillightsSyntaxColorVariables[selector]
    if (variable) return variable
  }
  return `--taillight-color-${selector
    .split('')
    .map(character => character.charCodeAt(0).toString(16))
    .join('-')}`
}

export function taillightsRenderingStyle(options?: TaillightsRenderingOptions): CSSProperties {
  const colors: Record<string, string> = {}
  for (const [selector, color] of Object.entries(options?.colors ?? {})) {
    colors[taillightsColorVariable(selector)] = color
  }
  return {
    ...colors,
    ...(options?.font === undefined ? {} : {'--taillight-font': options.font}),
    '--taillight-line-height': `${options?.lineHeight ?? TAILLIGHTS_LINE_HEIGHT}px`,
    '--taillight-padding': `${options?.paddingInline ?? TAILLIGHTS_PADDING_INLINE}px`,
    '--taillight-tab-size': options?.tabSize ?? 4,
    ...(options?.defaultColor === undefined ? {} : {'--fgColor-default': options.defaultColor}),
  }
}

export function validateTaillightsRendering(options?: TaillightsRenderingOptions) {
  if (options?.font !== undefined && !CSS.supports('font', options.font)) {
    throw new Error('Unsupported Taillight font.')
  }
  if (options?.lineHeight !== undefined && (!Number.isInteger(options.lineHeight) || options.lineHeight <= 0)) {
    throw new Error('Taillight lineHeight must be a positive whole number of CSS pixels.')
  }
  if (options?.paddingInline !== undefined && (!Number.isFinite(options.paddingInline) || options.paddingInline < 0)) {
    throw new Error('Taillight paddingInline must be non-negative.')
  }
  if (options?.tabSize !== undefined && (!Number.isInteger(options.tabSize) || options.tabSize <= 0)) {
    throw new Error('Taillight tabSize must be a positive whole number.')
  }
  if (options?.defaultColor !== undefined && !CSS.supports('color', options.defaultColor)) {
    throw new Error(`Unsupported Taillight foreground: ${options.defaultColor}`)
  }
}

export async function resolveTaillightsAppearance(container: HTMLElement, options?: TaillightsRenderingOptions) {
  validateTaillightsRendering(options)
  const probe = container.ownerDocument.createElement('div')
  probe.className = clsx(blockStyles.Block)
  probe.inert = true
  Object.assign(probe.style, {position: 'absolute', visibility: 'hidden', pointerEvents: 'none'})
  if (options?.font) probe.style.font = options.font
  if (options?.defaultColor) probe.style.setProperty('--fgColor-default', options.defaultColor)
  for (const [selector, color] of Object.entries(options?.colors ?? {})) {
    if (!CSS.supports('color', color)) throw new Error(`Unsupported Taillight foreground: ${color}`)
    probe.style.setProperty(taillightsColorVariable(selector), color)
  }
  container.append(probe)
  let appearance: ReturnType<typeof readTaillightsRenderingStyles>
  try {
    appearance = readTaillightsRenderingStyles(probe, options?.colors)
  } finally {
    probe.remove()
  }
  const fontSet = container.ownerDocument.fonts
  // Installed font variants need no extra load requests; missing webfont faces load concurrently.
  const pendingVariants = Object.values(appearance.fonts).filter(font => !fontSet.check(font))
  await Promise.all([appearance.font, ...pendingVariants].map(font => fontSet.load(font)))
  return {
    ...appearance,
    lineHeight: options?.lineHeight ?? TAILLIGHTS_LINE_HEIGHT,
    paddingLeft: options?.paddingInline ?? TAILLIGHTS_PADDING_INLINE,
  }
}

export type TaillightsAppearance = Awaited<ReturnType<typeof resolveTaillightsAppearance>>

export function readTaillightsRenderingStyles(element: HTMLElement, customColors?: TaillightsSyntaxColors) {
  const computedStyle = getComputedStyle(element)
  const defaultColor =
    computedStyle.getPropertyValue('--fgColor-default').trim() ||
    computedStyle.getPropertyValue('--color-fg-default').trim() ||
    computedStyle.color ||
    'rgb(31, 35, 40)'
  const colors = Object.fromEntries(
    [...new Set([...Object.keys(taillightsSyntaxColorVariables), ...Object.keys(customColors ?? {})])].map(selector => [
      selector,
      computedStyle.getPropertyValue(taillightsColorVariable(selector)).trim() || defaultColor,
    ]),
  )
  const font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`
  const fonts = {
    bold: `${computedStyle.fontStyle} bold ${computedStyle.fontSize} ${computedStyle.fontFamily}`,
    italic: `italic ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`,
    boldItalic: `italic bold ${computedStyle.fontSize} ${computedStyle.fontFamily}`,
  }
  return {colors, defaultColor, font, fonts}
}
